import { useState, useEffect } from 'react';
import {
  Users, Search, Loader2, AlertCircle, ChevronDown, ChevronUp,
  Trash2, Sparkles, Building2, Target, Zap, RefreshCw, Crosshair
} from 'lucide-react';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import useProspects from '../hooks/useProspects';
import { listProfiles } from '../api/scrape';

function ScoreBadge({ score }) {
  if (score == null) return <Badge variant="default">--</Badge>;
  if (score >= 90) return <Badge variant="success">{score}</Badge>;
  if (score >= 70) return <Badge variant="primary">{score}</Badge>;
  if (score >= 50) return <Badge variant="warning">{score}</Badge>;
  return <Badge variant="danger">{score}</Badge>;
}

function ICPSearchCard({ onSearch, searching, profiles, loadingProfiles, activeProfileId, onProfileChange }) {
  const [showManual, setShowManual] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [targetCount, setTargetCount] = useState(500);
  const [titles, setTitles] = useState('');
  const [keywords, setKeywords] = useState('');
  const [employeeRange, setEmployeeRange] = useState('');
  const [location, setLocation] = useState('');

  // Sync with external activeProfileId or auto-select latest
  useEffect(() => {
    if (activeProfileId) {
      setSelectedProfile(activeProfileId);
    } else if (profiles.length > 0 && !selectedProfile) {
      setSelectedProfile(profiles[0].jobId);
    }
  }, [profiles, selectedProfile, activeProfileId]);

  const handleICPSearch = () => {
    if (!selectedProfile) return;
    onSearch({ voiceProfileJobId: selectedProfile, targetCount });
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    onSearch({
      personTitles: titles ? titles.split(',').map(t => t.trim()) : [],
      keywords: keywords || undefined,
      employeeRanges: employeeRange ? [employeeRange] : [],
      locationIds: location ? [location] : [],
      perPage: 25,
      page: 1,
    });
  };

  const activeProfile = profiles.find(p => p.jobId === selectedProfile);

  return (
    <Card>
      <CardContent>
        {/* ICP-based search — primary action */}
        <div className="flex items-center gap-2 mb-4">
          <Crosshair className="w-4 h-4 text-primary-500" />
          <h3 className="text-sm font-semibold text-slate-900">Find Prospects from ICP</h3>
        </div>

        {loadingProfiles ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading voice profiles...
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-sm text-slate-500">
            No voice profiles yet. Go to <span className="font-medium text-primary-600">Voice Profile & ICP</span> first to analyze your company.
          </div>
        ) : (
          <div className="space-y-3">
            {profiles.length > 1 && (
              <select
                value={selectedProfile}
                onChange={(e) => {
                  setSelectedProfile(e.target.value);
                  onProfileChange(e.target.value);
                }}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
              >
                {profiles.map(p => (
                  <option key={p.jobId} value={p.jobId}>
                    {p.companyName} — {new Date(p.createdAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
            )}

            {activeProfile && (
              <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 rounded-lg">
                <Building2 className="w-4 h-4 text-primary-600 flex-shrink-0" />
                <span className="text-sm text-primary-700">
                  Using ICP from <span className="font-semibold">{activeProfile.companyName}</span>
                </span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button onClick={handleICPSearch} loading={searching} disabled={!selectedProfile}>
                <Crosshair className="w-4 h-4" />
                Search from ICP
              </Button>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500">Count:</label>
                <input
                  type="number"
                  min={10}
                  max={1000}
                  step={10}
                  value={targetCount}
                  onChange={(e) => setTargetCount(Math.min(1000, Math.max(10, Number(e.target.value) || 500)))}
                  className="w-20 text-sm border border-slate-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                onClick={() => setShowManual(!showManual)}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                {showManual ? 'Hide manual search' : 'Or search manually...'}
              </button>
            </div>
          </div>
        )}

        {/* Manual search — collapsed by default */}
        {showManual && (
          <form onSubmit={handleManualSearch} className="mt-4 pt-4 border-t border-slate-100 space-y-3">
            <p className="text-xs text-slate-400 uppercase font-medium">Manual Apollo Search</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Job Titles"
                placeholder="e.g. IT Director, CTO, Owner"
                hint="Comma-separated"
                value={titles}
                onChange={(e) => setTitles(e.target.value)}
              />
              <Input
                label="Keywords"
                placeholder="e.g. managed services, IT support"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
              />
              <Input
                label="Employee Range"
                placeholder="e.g. 11,50 or 51,200"
                value={employeeRange}
                onChange={(e) => setEmployeeRange(e.target.value)}
              />
              <Input
                label="Location"
                placeholder="e.g. United States"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" variant="secondary" loading={searching} disabled={!titles && !keywords}>
                <Search className="w-4 h-4" />
                Manual Search
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function BriefCard({ brief }) {
  if (!brief) return null;

  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
      {brief.companySummary && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Building2 className="w-3.5 h-3.5 text-primary-500" />
            <span className="text-xs font-medium text-slate-500 uppercase">Company Summary</span>
          </div>
          <p className="text-sm text-slate-600">{brief.companySummary}</p>
        </div>
      )}

      {brief.painPoints?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Target className="w-3.5 h-3.5 text-red-500" />
            <span className="text-xs font-medium text-slate-500 uppercase">Pain Points</span>
          </div>
          <ul className="space-y-1">
            {brief.painPoints.map((p, i) => (
              <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.talkingAngles?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-medium text-slate-500 uppercase">Talking Angles</span>
          </div>
          <ul className="space-y-1">
            {brief.talkingAngles.map((t, i) => (
              <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.personalizedHooks?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-medium text-slate-500 uppercase">Personalized Hooks</span>
          </div>
          <ul className="space-y-1">
            {brief.personalizedHooks.map((h, i) => (
              <li key={i} className="text-sm text-slate-600 italic">"{h}"</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ProspectRow({ prospect, selected, onToggle, onDelete, expanded, onExpand }) {
  return (
    <div className={`border rounded-lg transition-colors ${selected ? 'border-primary-300 bg-primary-50/30' : 'border-slate-200'}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-slate-900 truncate">{prospect.name || 'Unknown'}</p>
            <ScoreBadge score={prospect.priorityScore} />
          </div>
          <p className="text-xs text-slate-500 truncate">
            {prospect.title}{prospect.title && prospect.company ? ' at ' : ''}{prospect.company}
          </p>
        </div>
        <div className="hidden md:block text-right flex-shrink-0">
          <p className="text-xs text-slate-500">{prospect.industry}</p>
          <p className="text-xs text-slate-400">{prospect.city}{prospect.city && prospect.state ? ', ' : ''}{prospect.state}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={onExpand} className="p-1 text-slate-400 hover:text-slate-600">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={onDelete} className="p-1 text-slate-300 hover:text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><span className="text-slate-400">Email:</span> <span className="text-slate-600">{prospect.email || '—'}</span></div>
            <div><span className="text-slate-400">Employees:</span> <span className="text-slate-600">{prospect.employeeCount || '—'}</span></div>
            <div><span className="text-slate-400">Status:</span> <Badge variant={prospect.status === 'briefed' ? 'success' : 'default'}>{prospect.status}</Badge></div>
            <div><span className="text-slate-400">Added:</span> <span className="text-slate-600">{new Date(prospect.createdAt).toLocaleDateString()}</span></div>
          </div>
          <BriefCard brief={prospect.brief} />
        </div>
      )}
    </div>
  );
}

export default function Prospects() {
  const { prospects, total, loading, searching, briefing, error, activeProfileId, search, brief, remove, removeBatch, reload, filterByProfile } = useProspects();
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(true);

  // Load voice profiles on mount
  useEffect(() => {
    listProfiles()
      .then(data => setProfiles(data.profiles || []))
      .catch(() => {})
      .finally(() => setLoadingProfiles(false));
  }, []);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === prospects.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(prospects.map(p => p.prospectId)));
    }
  };

  const handleBrief = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    await brief(ids, undefined, activeProfileId || (profiles.length > 0 ? profiles[0].jobId : undefined));
  };

  const handleBatchDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setDeleting(true);
    await removeBatch(ids);
    setSelected(new Set());
    setDeleting(false);
  };

  const sorted = [...prospects].sort((a, b) => {
    if (sortBy === 'priorityScore') return (b.priorityScore || 0) - (a.priorityScore || 0);
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const newCount = prospects.filter(p => p.status === 'new').length;
  const briefedCount = prospects.filter(p => p.status === 'briefed').length;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Prospects</h1>
          <p className="text-sm text-slate-500 mt-1">
            Discover and manage prospects matching your ICP.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => reload()}>
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      <ICPSearchCard
        onSearch={search}
        searching={searching}
        profiles={profiles}
        loadingProfiles={loadingProfiles}
        activeProfileId={activeProfileId}
        onProfileChange={filterByProfile}
      />

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {searching && (
        <Card>
          <CardContent className="py-8 text-center">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-3" />
            <h3 className="text-base font-semibold text-slate-900 mb-1">Searching Apollo...</h3>
            <p className="text-sm text-slate-500">Finding and enriching up to 500 prospects from your ICP. This may take a few minutes.</p>
            {total > 0 && (
              <p className="text-lg font-bold text-primary-600 mt-3">{total} prospects found so far</p>
            )}
          </CardContent>
        </Card>
      )}

      {loading && !searching ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Loading prospects...</p>
          </CardContent>
        </Card>
      ) : !searching && prospects.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">No Prospects Yet</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Click "Search from ICP" above to automatically find prospects matching your ideal customer profile.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats & actions bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">{total} prospects</span>
              <Badge variant="default">{newCount} new</Badge>
              <Badge variant="success">{briefedCount} briefed</Badge>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500"
              >
                <option value="createdAt">Newest First</option>
                <option value="priorityScore">Highest Score</option>
              </select>
              {selected.size > 0 && (
                <>
                  <Button size="sm" loading={briefing} onClick={handleBrief}>
                    <Sparkles className="w-4 h-4" />
                    Generate Briefs ({selected.size})
                  </Button>
                  <Button size="sm" variant="danger" loading={deleting} onClick={handleBatchDelete}>
                    <Trash2 className="w-4 h-4" />
                    Delete ({selected.size})
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Select all */}
          <div className="flex items-center gap-2 px-1">
            <input
              type="checkbox"
              checked={selected.size === prospects.length && prospects.length > 0}
              onChange={toggleAll}
              className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-xs text-slate-500">
              {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
            </span>
          </div>

          {/* Prospect list */}
          <div className="space-y-2">
            {sorted.map(p => (
              <ProspectRow
                key={p.prospectId}
                prospect={p}
                selected={selected.has(p.prospectId)}
                onToggle={() => toggleSelect(p.prospectId)}
                onDelete={() => remove(p.prospectId)}
                expanded={expandedId === p.prospectId}
                onExpand={() => setExpandedId(expandedId === p.prospectId ? null : p.prospectId)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
